/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Authentication Hooks
 *
 * React Query hooks for authentication API calls.
 * Can be used in any component that needs auth functionality.
 *
 * Flow:
 * 1. Login → get user data
 * 2. Load permissions for user's role
 * 3. Store both in respective stores
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, roleApi } from '@/api';
import { useAuthStore, usePermissionStore } from '@/store';
import { LoginRequest } from '@/features/auth/types';
import { logger } from '@/shared/utils/logger';

// ============================================================================
// Constants
// ============================================================================

// Cache times in milliseconds
const STALE_TIME_5_MIN = 300000; // 5 minutes (5 * 60 * 1000)

// ============================================================================
// Query Keys
// ============================================================================

export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'currentUser'] as const,
};

// ============================================================================
// Queries
// ============================================================================

/**
 * Hook to get current authenticated user
 */
export const useCurrentUser = () => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  return useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: () => authApi.getCurrentUser(),
    enabled: isAuthenticated,
    staleTime: STALE_TIME_5_MIN,
  });
};

// ============================================================================
// Mutations
// ============================================================================

/**
 * Hook for login mutation
 */
export const useLoginMutation = () => {
  const queryClient = useQueryClient();
  const storeLogin = useAuthStore(state => state.login);
  const setPermissions = usePermissionStore(state => state.setPermissions);

  return useMutation({
    // Disable retry for login - fail fast on network errors
    retry: false,
    mutationFn: async (credentials: LoginRequest) => {
      const response = await authApi.login(credentials);

      // Validate response structure BEFORE onSuccess
      if (!response || response.success !== 'Success') {
        // Backend returned error - preserve error structure
        const error = new Error(response?.error?.message || 'Login failed');
        // Attach backend error code and full error object for better handling
        (error as any).code = response?.error?.code;
        (error as any).backendError = response?.error;
        throw error;
      }

      // Validate required fields
      const { user } = response;
      if (!user) {
        const error = new Error('Invalid response: missing user data');
        (error as any).code = 'INVALID_RESPONSE';
        throw error;
      }

      return response;
    },
    onSuccess: async response => {
      // Response already validated in mutationFn, safe to use
      const { user } = response;

      if (!user) {
        return; // Type guard
      }

      // Map user to shared User type
      const mappedUser = {
        userId: user.userId,
        userName: user.userName,
        email: user.email,
        companyCode: user.companyCode,
        companyName: user.companyName,
        role: user.role || 'user',
        phone: user.phone,
        department: user.department,
      };

      // Update auth store (tokens are in HttpOnly cookies)
      storeLogin(mappedUser);

      // Load permissions and menu for user's role
      try {
        const roleCode = user.role || 'U'; // Default to 'U' (User) if no role
        logger.info('Loading permissions for role', { roleCode });

        const permissionData = await roleApi.getPermissionsByRole(roleCode);
        setPermissions(
          permissionData.roleCode,
          permissionData.permissions,
          permissionData.allowedFunctions,
          permissionData.allowedNodeIds,
          permissionData.menuList,
          permissionData.isAdmin,
        );

        logger.info('Permissions loaded successfully', {
          roleCode: permissionData.roleCode,
          functionCount: permissionData.allowedFunctions.length,
          menuCount: permissionData.menuList.length,
          isAdmin: permissionData.isAdmin,
        });
      } catch (error) {
        // Log error but don't fail login - user can still access with limited permissions
        logger.error('Failed to load permissions', error as Error);
        // Set empty permissions - will restrict access (isAdmin = false)
        setPermissions(user.role || 'U', [], [], [], [], false);
      }

      // Invalidate user queries to refetch with new token
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser() });
    },
  });
};

/**
 * Hook for logout mutation
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const logout = useAuthStore(state => state.logout);
  const clearPermissions = usePermissionStore(state => state.clearPermissions);

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      clearPermissions();
      logout();
      queryClient.clear();
    },
    onError: () => {
      // Even if API fails, clear local state
      clearPermissions();
      logout();
      queryClient.clear();
    },
  });
};

/**
 * Hook for SSO callback
 * Handles SAML response from OneLogin
 */
export const useSsoCallback = () => {
  const queryClient = useQueryClient();
  const storeLogin = useAuthStore(state => state.login);
  const setPermissions = usePermissionStore(state => state.setPermissions);

  return useMutation({
    mutationFn: async (params: {
      SAMLResponse: string;
      RelayState?: string;
    }) => {
      // API returns LoginResponse directly (not wrapped in ApiResponse)
      const response = await authApi.ssoCallback(params);

      // Validate response structure
      if (!response || response.success !== 'Success') {
        const error = new Error(response?.error?.message || 'SSO login failed');
        (error as any).code = response?.error?.code;
        (error as any).backendError = response?.error;
        throw error;
      }

      // Validate required fields
      const { user } = response;
      if (!user) {
        const error = new Error('Invalid SSO response: missing user data');
        (error as any).code = 'INVALID_SSO_RESPONSE';
        throw error;
      }

      return response;
    },
    onSuccess: async response => {
      // Response already validated in mutationFn
      const { user } = response;
      if (!user) {
        return;
      }

      const mappedUser = {
        userId: user.userId,
        userName: user.userName || user.userId,
        email: user.email || '',
        companyCode: user.companyCode,
        companyName: user.companyName,
        role: user.role || 'user',
        phone: user.phone,
        department: user.department || '',
      };

      // Update auth store (tokens are in HttpOnly cookies)
      storeLogin(mappedUser);

      // Load permissions and menu for user's role
      try {
        const roleCode = user.role || 'U';
        logger.info('Loading permissions for role (SSO)', { roleCode });

        const permissionData = await roleApi.getPermissionsByRole(roleCode);
        setPermissions(
          permissionData.roleCode,
          permissionData.permissions,
          permissionData.allowedFunctions,
          permissionData.allowedNodeIds,
          permissionData.menuList,
          permissionData.isAdmin,
        );

        logger.info('Permissions loaded successfully (SSO)', {
          roleCode: permissionData.roleCode,
          functionCount: permissionData.allowedFunctions.length,
          menuCount: permissionData.menuList.length,
          isAdmin: permissionData.isAdmin,
        });
      } catch (error) {
        logger.error('Failed to load permissions (SSO)', error as Error);
        setPermissions(user.role || 'U', [], [], [], [], false);
      }

      queryClient.invalidateQueries({ queryKey: authKeys.currentUser() });
    },
  });
};
